interface TerritorySectionProps {
  onCTAClick: () => void;
}

const US_STATES = [
  { abbr: "AL", name: "Alabama", status: "available" },
  { abbr: "AK", name: "Alaska", status: "available" },
  { abbr: "AZ", name: "Arizona", status: "limited" },
  { abbr: "AR", name: "Arkansas", status: "available" },
  { abbr: "CA", name: "California", status: "limited" },
  { abbr: "CO", name: "Colorado", status: "limited" },
  { abbr: "CT", name: "Connecticut", status: "available" },
  { abbr: "DE", name: "Delaware", status: "available" },
  { abbr: "FL", name: "Florida", status: "limited" },
  { abbr: "GA", name: "Georgia", status: "limited" },
  { abbr: "HI", name: "Hawaii", status: "available" },
  { abbr: "ID", name: "Idaho", status: "available" },
  { abbr: "IL", name: "Illinois", status: "limited" },
  { abbr: "IN", name: "Indiana", status: "available" },
  { abbr: "IA", name: "Iowa", status: "available" },
  { abbr: "KS", name: "Kansas", status: "available" },
  { abbr: "KY", name: "Kentucky", status: "available" },
  { abbr: "LA", name: "Louisiana", status: "available" },
  { abbr: "ME", name: "Maine", status: "available" },
  { abbr: "MD", name: "Maryland", status: "limited" },
  { abbr: "MA", name: "Massachusetts", status: "available" },
  { abbr: "MI", name: "Michigan", status: "available" },
  { abbr: "MN", name: "Minnesota", status: "available" },
  { abbr: "MS", name: "Mississippi", status: "available" },
  { abbr: "MO", name: "Missouri", status: "available" },
  { abbr: "MT", name: "Montana", status: "available" },
  { abbr: "NE", name: "Nebraska", status: "available" },
  { abbr: "NV", name: "Nevada", status: "limited" },
  { abbr: "NH", name: "New Hampshire", status: "available" },
  { abbr: "NJ", name: "New Jersey", status: "limited" },
  { abbr: "NM", name: "New Mexico", status: "available" },
  { abbr: "NY", name: "New York", status: "limited" },
  { abbr: "NC", name: "North Carolina", status: "limited" },
  { abbr: "ND", name: "North Dakota", status: "available" },
  { abbr: "OH", name: "Ohio", status: "available" },
  { abbr: "OK", name: "Oklahoma", status: "available" },
  { abbr: "OR", name: "Oregon", status: "available" },
  { abbr: "PA", name: "Pennsylvania", status: "limited" },
  { abbr: "RI", name: "Rhode Island", status: "available" },
  { abbr: "SC", name: "South Carolina", status: "available" },
  { abbr: "SD", name: "South Dakota", status: "available" },
  { abbr: "TN", name: "Tennessee", status: "limited" },
  { abbr: "TX", name: "Texas", status: "limited" },
  { abbr: "UT", name: "Utah", status: "available" },
  { abbr: "VT", name: "Vermont", status: "available" },
  { abbr: "VA", name: "Virginia", status: "limited" },
  { abbr: "WA", name: "Washington", status: "available" },
  { abbr: "WV", name: "West Virginia", status: "available" },
  { abbr: "WI", name: "Wisconsin", status: "available" },
  { abbr: "WY", name: "Wyoming", status: "available" },
];

const statusConfig = {
  available: {
    label: "Available",
    dot: "bg-green-400",
    card: "border-green-100 bg-white",
    badge: "text-green-700 bg-green-50 border-green-200",
  },
  limited: {
    label: "Limited",
    dot: "bg-orange-400",
    card: "border-orange-100 bg-orange-50/30",
    badge: "text-orange-700 bg-orange-50 border-orange-200",
  },
};

export default function TerritorySection({ onCTAClick }: TerritorySectionProps) {
  const availableCount = US_STATES.filter((s) => s.status === "available").length;
  const limitedCount = US_STATES.filter((s) => s.status === "limited").length;

  return (
    <section id="territory" className="bg-alt-bg py-14 md:py-24 px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14">
          <div className="inline-flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-1.5 mb-6">
            <i className="ri-map-pin-line text-navy text-xs" />
            <span className="text-xs font-semibold text-navy">Territory Control</span>
          </div>
          <h2
            className="text-navy font-semibold mb-5"
            style={{ fontSize: "clamp(26px, 3vw, 38px)", lineHeight: "1.2" }}
          >
            Secure Your Market Before It&apos;s Gone
          </h2>
          <p className="text-[#4a5568] text-lg" style={{ lineHeight: "1.65" }}>
            Choose your state or start with a single county — once your territory is claimed, it&apos;s permanently locked.
          </p>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-3 max-w-2xl mx-auto mb-8 md:mb-12">
          <div className="bg-white rounded-2xl p-4 md:p-5 text-center border border-gray-100">
            <p className="text-navy text-2xl md:text-3xl font-extrabold">50</p>
            <p className="text-[#4a5568] text-xs mt-1 font-medium">Active States</p>
          </div>
          <div className="bg-white rounded-2xl p-4 md:p-5 text-center border border-green-100">
            <p className="text-green-600 text-2xl md:text-3xl font-extrabold">{availableCount}</p>
            <p className="text-[#4a5568] text-xs mt-1 font-medium">Fully Available</p>
          </div>
          <div className="bg-white rounded-2xl p-4 md:p-5 text-center border border-orange-100">
            <p className="text-orange-500 text-2xl md:text-3xl font-extrabold">{limitedCount}</p>
            <p className="text-[#4a5568] text-xs mt-1 font-medium">Limited Spots</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 items-start">
          {/* Left: Territory types */}
          <div className="lg:col-span-1">
            <h3 className="text-navy font-semibold text-lg mb-6">Territory Options</h3>

            <div className="flex flex-col gap-4 mb-8">
              <div className="bg-white rounded-xl p-5 border border-gray-100">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-navy rounded-lg flex-shrink-0">
                    <i className="ri-award-line text-white text-sm" />
                  </div>
                  <div>
                    <h4 className="text-navy font-semibold text-sm mb-1">Exclusive Market Control (State or County)</h4>
                    <p className="text-[#4a5568] text-xs leading-relaxed">
                      You are the only Imperium partner in your territory. Every triggered call, every opportunity — all yours.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-5 border border-gray-100">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-primary rounded-lg flex-shrink-0">
                    <i className="ri-map-2-line text-white text-sm" />
                  </div>
                  <div>
                    <h4 className="text-navy font-semibold text-sm mb-1">Start Local. Expand to Dominate.</h4>
                    <p className="text-[#4a5568] text-xs leading-relaxed font-medium mb-1">Start with a County. Own the State Next.</p>
                    <p className="text-[#4a5568] text-xs leading-relaxed">
                      Lock in a high-value county today — expand to statewide control as you grow.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Legend */}
            <div className="flex flex-col gap-2 mb-8">
              <p className="text-[#4a5568] text-xs font-semibold uppercase tracking-widest mb-2">Legend</p>
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-green-400 flex-shrink-0" />
                <span className="text-[#4a5568] text-xs">Available — secure now</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-orange-400 flex-shrink-0" />
                <span className="text-[#4a5568] text-xs">Limited spots remaining</span>
              </div>
            </div>

            <button
              onClick={onCTAClick}
              className="inline-flex items-center gap-2 bg-primary text-white font-semibold px-7 py-3.5 rounded-lg hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap w-full justify-center"
            >
              Check Your State
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm" />
              </span>
            </button>
          </div>

          {/* Right: State grid */}
          <div className="lg:col-span-2">
            <p className="text-[#4a5568] text-xs font-semibold uppercase tracking-widest mb-4">
              All 50 States — Territory Availability
            </p>
            <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2.5">
              {US_STATES.map((state) => {
                const cfg = statusConfig[state.status as keyof typeof statusConfig];
                return (
                  <div
                    key={state.abbr}
                    title={`${state.name} — ${cfg.label}`}
                    className={`rounded-xl p-3 border flex flex-col items-center justify-center gap-1.5 cursor-default transition-transform hover:scale-105 min-h-[54px] ${cfg.card}`}
                  >
                    <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
                    <span className="text-navy font-bold text-[13px] leading-none">{state.abbr}</span>
                  </div>
                );
              })}
            </div>
            <p className="text-[#4a5568] text-xs mt-4 text-center">
              Updated daily · Active in all 50 states
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
